import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MarketLayout from '../components/MarketLayout';
import RightDrawer from '../components/RightDrawer';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PRIORITY_STYLES = {
  urgent: { bg: '#ff082f', color: '#ffffff' },
  high: { bg: '#ffe6e9', color: '#ff082f' },
  normal: { bg: '#f3f4f6', color: '#6b7280' },
  low: { bg: '#f9fafb', color: '#9ca3af' },
};

const TYPE_LABELS = {
  loan: 'Loan',
  investor: 'Investor',
  funding: 'Funding',
  system: 'System',
};

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function MarketAlerts() {
  const token = localStorage.getItem('doorly_admin_token');
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('unread');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/market/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          priority: priorityFilter || undefined,
          status: statusFilter || undefined,
          search: search || undefined,
          page,
          limit: 50,
        },
      });
      setAlerts(res.data.alerts || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [token, priorityFilter, statusFilter, search, page]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/api/admin/market/alerts/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts((prev) => prev.map((a) => a.id === id ? { ...a, is_read: 1 } : a));
      if (selected?.id === id) setSelected((prev) => ({ ...prev, is_read: 1 }));
    } catch {}
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      await axios.put(`${API}/api/admin/market/alerts/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchAlerts();
    } catch {}
    setMarkingAll(false);
  };

  const openDrawer = (alert) => {
    setSelected(alert);
    if (!alert.is_read) markRead(alert.id);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <MarketLayout>
      <div style={{ padding: '28px 32px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: 0 }}>Market Alerts</h1>
            <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0' }}>{total} {statusFilter || 'total'} alert{total !== 1 ? 's' : ''}</p>
          </div>
          {statusFilter !== 'read' && (
            <button onClick={markAllRead} disabled={markingAll}
              style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151', opacity: markingAll ? 0.6 : 1 }}>
              {markingAll ? 'Marking...' : 'Mark all read'}
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search alerts..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ height: '36px', padding: '0 12px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none', width: '220px' }} />
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
            <option value="">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
          <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
            style={{ height: '36px', padding: '0 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
            <option value="">All Priorities</option>
            {['urgent', 'high', 'normal', 'low'].map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>

        {/* Table */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                {['Type', 'Description', 'Related Record', 'Priority', 'Date', 'Status'].map((h) => (
                  <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f3f4f6' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading...</td></tr>
              ) : alerts.length === 0 ? (
                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No alerts found</td></tr>
              ) : (
                alerts.map((alert) => (
                  <tr key={alert.id} onClick={() => openDrawer(alert)} style={{ borderBottom: '1px solid #f9fafb', cursor: 'pointer', backgroundColor: !alert.is_read ? '#fefefe' : 'transparent' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = !alert.is_read ? '#fefefe' : 'transparent'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: '#eff6ff', color: '#3a6ad9' }}>
                        {TYPE_LABELS[alert.type] || alert.type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '13px', color: '#374151', fontWeight: alert.is_read ? '400' : '500', maxWidth: '300px' }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{alert.description}</div>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: '#9ca3af' }}>{alert.related_record || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: PRIORITY_STYLES[alert.priority || 'normal']?.bg, color: PRIORITY_STYLES[alert.priority || 'normal']?.color }}>
                        {alert.priority || 'normal'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>{fmtDate(alert.created_at)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: alert.is_read ? '#f3f4f6' : '#d4f9a0', color: alert.is_read ? '#6b7280' : '#acf44e' }}>
                        {alert.is_read ? 'Read' : 'Unread'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Page {page} of {totalPages} · {total} total</span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '12px', opacity: page === 1 ? 0.4 : 1 }}>Prev</button>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '12px', opacity: page === totalPages ? 0.4 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Detail Drawer */}
      <RightDrawer open={!!selected} onClose={() => setSelected(null)} title="Alert Detail" width={400}>
        {selected && (
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: '#eff6ff', color: '#3a6ad9' }}>
                  {TYPE_LABELS[selected.type] || selected.type}
                </span>
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: PRIORITY_STYLES[selected.priority || 'normal']?.bg, color: PRIORITY_STYLES[selected.priority || 'normal']?.color }}>
                  {selected.priority || 'normal'}
                </span>
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '9999px', backgroundColor: selected.is_read ? '#f3f4f6' : '#d4f9a0', color: selected.is_read ? '#6b7280' : '#acf44e' }}>
                  {selected.is_read ? 'Read' : 'Unread'}
                </span>
              </div>
              <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '14px' }}>
                <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', margin: 0 }}>{selected.description}</p>
              </div>
            </div>

            {selected.related_record && (
              <div style={{ marginBottom: '16px', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600' }}>RELATED RECORD</span>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#3a6ad9', marginTop: '4px' }}>{selected.related_record}</div>
              </div>
            )}

            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Created</div>
            <div style={{ fontSize: '13px', color: '#374151', marginBottom: '16px' }}>{fmtDate(selected.created_at)}</div>

            {!selected.is_read && (
              <button onClick={() => markRead(selected.id)}
                style={{ width: '100%', padding: '9px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                Mark as Read
              </button>
            )}
          </div>
        )}
      </RightDrawer>
    </MarketLayout>
  );
}
